/** Compare dotted numeric versions ("1.0.6" vs "2.0"). Returns -1 / 0 / 1. Non-numeric parts ignored. */
export function cmpVersion(a: string, b: string): number {
  const parse = (s: string) => s.split(/[^\d]+/).filter(Boolean).map(Number);
  const pa = parse(a);
  const pb = parse(b);
  const n = Math.max(pa.length, pb.length);
  for (let i = 0; i < n; i++) {
    const x = pa[i] ?? 0;
    const y = pb[i] ?? 0;
    if (x !== y) return x < y ? -1 : 1;
  }
  return 0;
}

/**
 * Vendor keys accepted by /device/finger/library-{installed,available}/{vendor}. The Device Service
 * upper-cases and switches on these (FrictionRidgeService.isInstalled), so anything else returns an
 * error string rather than a version.
 */
export const LIBRARY_VENDORS = ['IB', 'THALES', 'CROSSMATCH', 'SUPREMA'] as const;
export type LibraryVendor = (typeof LIBRARY_VENDORS)[number];

/** Device-group name ("IB Kojak", "Thales DactyScan") -> the vendor key its library check uses. */
export function vendorOfGroup(group: string | null | undefined): LibraryVendor | null {
  const first = (group ?? '').trim().split(/\s+/)[0]?.toUpperCase() ?? '';
  return (LIBRARY_VENDORS as readonly string[]).includes(first) ? (first as LibraryVendor) : null;
}

/**
 * The installed library version, or null when it can't be determined.
 *
 * /device/finger/library-installed returns the version when the library is present but a human-readable
 * sentence otherwise — "Error checking IB installation (Was the correct vendor selected?)". That must not
 * be treated as a version: cmpVersion strips non-digits, so the sentence parses as 0.0.0 and would read
 * as "below the required minimum", warning customers whose library is fine (or who simply don't use that
 * vendor) that they must update.
 */
export function parseLibraryVersion(raw: string | null | undefined): string | null {
  const v = (raw ?? '').trim().replace(/^v/i, '');
  return /^\d+(\.\d+)*$/.test(v) ? v : null;
}
